import { type CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Carrier, CreateCarrierInput, PatchCarrierInput } from '@magimp/shared';
import {
  Button,
  EditorModal,
  Input,
  Textarea,
  useEditorGuard,
  useNarrow,
} from '../../../ui/index.js';

// WHY: on narrow viewports the three footer actions stack into
//   full-width buttons. `Button` is `inline-flex` by default so the
//   `display: flex` + `width: 100%` override turns each into a block
//   tap target without changing the shared primitive.
const NARROW_ACTION_STYLE: CSSProperties = {
  display: 'flex',
  width: '100%',
  justifyContent: 'center',
};
import {
  activateCarrier,
  deactivateCarrier,
  fetchCarrierCatalogItem,
  patchCarrierCatalog,
} from '../../../api/requests/index.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import { CarrierWarehouseSection } from './CarrierWarehouseSection.js';
import { CarrierPriceListSection } from './CarrierPriceListSection.js';
import { FieldGrid, Labeled } from './CarrierEditorModal.parts.js';

export function CarrierEditorModal({
  carrier,
  onClose,
  onUpdated,
}: {
  carrier: Carrier;
  onClose: () => void;
  onUpdated: (carrier: Carrier) => void;
}) {
  const { t } = useTranslation(['carriers', 'common']);
  const qc = useQueryClient();
  const showToast = useToastStore((s) => s.show);
  // INVARIANT: on narrow viewports the three footer actions are
  //   stacked top-to-bottom (Save → Deactivate/Reactivate → Close) as
  //   full-width buttons so long Russian labels never overflow. The
  //   generic `Modal.footer` keeps its horizontal-row default — we
  //   override the footer locally rather than reshape the global slot.
  const narrow = useNarrow();
  const [form, setForm] = useState<CreateCarrierInput>({
    name: carrier.name,
    email: carrier.email,
    phone: carrier.phone,
    notes: carrier.notes,
    active: carrier.active,
  });
  // INVARIANT: dirty is aggregated across the parent form and BOTH nested
  // sections into a single shared EditorModal wrapper guard. Children report
  // only primitive booleans via `onDirtyChange` — never their form state.
  const [warehouseDirty, setWarehouseDirty] = useState(false);
  const [priceListDirty, setPriceListDirty] = useState(false);
  const save = useMutation({
    mutationFn: (body: PatchCarrierInput) => patchCarrierCatalog(carrier.id, body),
    onSuccess: (updated) => {
      onUpdated(updated);
      setForm({
        name: updated.name,
        email: updated.email,
        phone: updated.phone,
        notes: updated.notes,
        active: updated.active,
      });
      void qc.invalidateQueries({ queryKey: ['carriers-catalog'] });
      showToast({ tone: 'success', text: t('toasts.updated') });
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });
  const toggle = useMutation({
    mutationFn: () =>
      carrier.active ? deactivateCarrier(carrier.id) : activateCarrier(carrier.id),
    onSuccess: (updated) => {
      onUpdated(updated);
      setForm((current) => ({ ...current, active: updated.active }));
      void qc.invalidateQueries({ queryKey: ['carriers-catalog'] });
      showToast({
        tone: 'success',
        text: updated.active ? t('toasts.activated') : t('toasts.deactivated'),
      });
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const refreshCarrier = async (): Promise<void> => {
    onUpdated(await fetchCarrierCatalogItem(carrier.id));
    void qc.invalidateQueries({ queryKey: ['carriers-catalog'] });
    void qc.invalidateQueries({ queryKey: ['price-lists'] });
    void qc.invalidateQueries({ queryKey: ['warehouses'] });
  };

  const parentDirty = isCarrierEditorParentDirty(form, carrier);
  const dirty = parentDirty || warehouseDirty || priceListDirty;
  // WHY: parent-only busy. Nested sections own their own mutations; their
  // local mutation handles do not bubble up. The provider's route blocker
  // still aggregates dirty across all registered guards, so an unmount
  // race during a nested in-flight save would surface as a regular dirty
  // prompt rather than be silently skipped.
  const busy = save.isPending || toggle.isPending;

  return (
    <EditorModal
      open
      title={t('modals.editTitle', { name: carrier.name })}
      width={760}
      dirty={dirty}
      busy={busy}
      onConfirmClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <CarrierForm form={form} onChange={setForm} />
        <CarrierWarehouseSection
          carrier={carrier}
          onRefresh={refreshCarrier}
          onDirtyChange={setWarehouseDirty}
        />
        <CarrierPriceListSection
          carrier={carrier}
          onRefresh={refreshCarrier}
          onDirtyChange={setPriceListDirty}
        />
      </div>
      <EditorFooter narrow={narrow}>
        {narrow ? (
          <div
            data-testid="carrier-editor-narrow-actions"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              width: '100%',
            }}
          >
            <Button
              kind="primary"
              style={NARROW_ACTION_STYLE}
              disabled={!form.name.trim() || save.isPending}
              onClick={() => save.mutate(normalizeCarrierPatch(form))}
            >
              {t('common:actions.save')}
            </Button>
            <Button
              kind={carrier.active ? 'danger' : 'secondary'}
              style={NARROW_ACTION_STYLE}
              onClick={() => toggle.mutate()}
            >
              {carrier.active ? t('common:actions.deactivate') : t('common:actions.reactivate')}
            </Button>
            <CloseButton
              onClose={onClose}
              label={t('common:actions.close')}
              style={NARROW_ACTION_STYLE}
            />
          </div>
        ) : (
          <>
            <CloseButton onClose={onClose} label={t('common:actions.close')} />
            <Button kind={carrier.active ? 'danger' : 'secondary'} onClick={() => toggle.mutate()}>
              {carrier.active ? t('common:actions.deactivate') : t('common:actions.reactivate')}
            </Button>
            <Button
              kind="primary"
              disabled={!form.name.trim() || save.isPending}
              onClick={() => save.mutate(normalizeCarrierPatch(form))}
            >
              {t('common:actions.save')}
            </Button>
          </>
        )}
      </EditorFooter>
    </EditorModal>
  );
}

// WHY: Close must call `useEditorGuard().confirmDestructive(onClose)` to
// prompt on dirty close. The EditorGuardContext provider only wraps the
// EditorModal `children` subtree, not the `footer` prop, so the action
// buttons are rendered inline at the bottom of children. `EditorFooter`
// mirrors the visual style of the Modal footer slot.
function EditorFooter({ narrow, children }: { narrow: boolean; children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 12,
        marginLeft: -16,
        marginRight: -16,
        marginBottom: -16,
        padding: '12px 16px',
        borderTop: '1px solid var(--border-2)',
        display: 'flex',
        gap: 8,
        justifyContent: narrow ? 'stretch' : 'flex-end',
      }}
    >
      {children}
    </div>
  );
}

function CloseButton({
  onClose,
  label,
  style,
}: {
  onClose: () => void;
  label: string;
  style?: CSSProperties;
}) {
  const { confirmDestructive } = useEditorGuard();
  if (style) {
    return (
      <Button kind="ghost" style={style} onClick={() => confirmDestructive(onClose)}>
        {label}
      </Button>
    );
  }
  return (
    <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
      {label}
    </Button>
  );
}

function normalizeCarrierForm(form: CreateCarrierInput): CreateCarrierInput {
  return {
    name: form.name.trim(),
    email: form.email?.trim() ? form.email.trim() : null,
    phone: form.phone?.trim() ? form.phone.trim() : null,
    notes: form.notes.trim(),
    active: form.active,
  };
}

// WHY: compare normalized form against the server baseline so a typed +
// reverted edit (incl. trailing-whitespace-only typing) closes without
// warning. Mirrors the diff semantics of the save mutation.
function isCarrierEditorParentDirty(form: CreateCarrierInput, baseline: Carrier): boolean {
  const current = normalizeCarrierForm(form);
  const ref = normalizeCarrierForm({
    name: baseline.name,
    email: baseline.email,
    phone: baseline.phone,
    notes: baseline.notes,
    active: baseline.active,
  });
  return (
    current.name !== ref.name ||
    current.email !== ref.email ||
    current.phone !== ref.phone ||
    current.notes !== ref.notes
  );
}

function normalizeCarrierPatch(form: CreateCarrierInput): PatchCarrierInput {
  const normalized = normalizeCarrierForm(form);
  return {
    name: normalized.name,
    email: normalized.email,
    phone: normalized.phone,
    notes: normalized.notes,
  };
}

function CarrierForm({
  form,
  onChange,
}: {
  form: CreateCarrierInput;
  onChange: (form: CreateCarrierInput) => void;
}) {
  const { t } = useTranslation('carriers');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <FieldGrid>
        <Labeled label={t('fields.name')} required>
          <Input value={form.name} onChange={(name) => onChange({ ...form, name })} />
        </Labeled>
        <Labeled label={t('fields.email')}>
          <Input
            value={form.email ?? ''}
            onChange={(email) => onChange({ ...form, email })}
            placeholder={t('fields.optional')}
          />
        </Labeled>
        <Labeled label={t('fields.phone')}>
          <Input
            value={form.phone ?? ''}
            onChange={(phone) => onChange({ ...form, phone })}
            placeholder={t('fields.optional')}
          />
        </Labeled>
      </FieldGrid>
      <Labeled label={t('fields.notes')}>
        <Textarea
          value={form.notes}
          onChange={(notes) => onChange({ ...form, notes })}
          placeholder={t('fields.optional')}
        />
      </Labeled>
    </div>
  );
}
